import { createContext, use, useCallback, useMemo, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AuthResponse,
  LoginRequest,
  PublicUser,
  RegisterRequest,
  RegistrationStatus,
  SessionResponse,
} from '@pglearn/shared';
import { api } from './api.js';

interface AuthContextValue {
  user: PublicUser | null;
  /** True only while the initial session probe is in flight. */
  isLoading: boolean;
  /**
   * Whether self-service sign-up is open, or `null` while that is still being
   * fetched. Callers that only hide a link should treat `null` as open —
   * flashing "Sign up" away on every page load is worse than showing it for a
   * moment on the rare occasion registration is paused.
   */
  registrationOpen: boolean | null;
  login: (input: LoginRequest) => Promise<void>;
  register: (input: RegisterRequest) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const session = useQuery({
    queryKey: ['auth', 'me'],
    // /auth/session rather than /auth/me: not being signed in is a normal
    // state, and asking a protected route about it put a 401 in every
    // anonymous visitor's console on every page load.
    queryFn: async () => (await api.get<SessionResponse>('/auth/session')).user,
    retry: false,
    staleTime: 5 * 60_000,
  });

  // Server-controlled and changes only on a restart, so there is no reason to
  // re-fetch it as the learner moves around.
  const registration = useQuery({
    queryKey: ['auth', 'registration'],
    queryFn: () => api.get<RegistrationStatus>('/auth/registration'),
    retry: false,
    staleTime: Infinity,
  });

  const loginMutation = useMutation({
    mutationFn: (input: LoginRequest) => api.post<AuthResponse>('/auth/login', input),
    onSuccess: (response) => queryClient.setQueryData(['auth', 'me'], response.user),
  });

  const registerMutation = useMutation({
    mutationFn: (input: RegisterRequest) => api.post<AuthResponse>('/auth/register', input),
    onSuccess: (response) => queryClient.setQueryData(['auth', 'me'], response.user),
  });

  const login = useCallback(
    async (input: LoginRequest) => {
      await loginMutation.mutateAsync(input);
    },
    [loginMutation],
  );

  const register = useCallback(
    async (input: RegisterRequest) => {
      await registerMutation.mutateAsync(input);
    },
    [registerMutation],
  );

  const logout = useCallback(async () => {
    await api.post('/auth/logout');
    queryClient.setQueryData(['auth', 'me'], null);
    // Everything cached was scoped to that user; keeping it would leak one
    // learner's progress into the next session on a shared machine.
    await queryClient.invalidateQueries();
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user: session.data ?? null,
      isLoading: session.isLoading,
      // A failed probe leaves this null rather than false: the sign-up page
      // should not claim registration is closed because one request failed.
      registrationOpen: registration.data?.open ?? null,
      login,
      register,
      logout,
    }),
    [session.data, session.isLoading, registration.data, login, register, logout],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthContextValue {
  const context = use(AuthContext);
  if (!context) throw new Error('useAuth must be used inside an AuthProvider');
  return context;
}
